import { Badge, Box, Collapse, Flex, Text } from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { useState } from "react";
import { ApiEndpoint } from "./types";
import { MethodBadge } from "./MethodBadge";
import { CodeBlock } from "./CodeBlock";

export function EndpointRow({ endpoint }: { endpoint: ApiEndpoint }) {
  const [open, setOpen] = useState(false);

  return (
    <Box
      borderBottom="1px solid"
      borderColor="gray.100"
      _last={{ borderBottom: "none" }}
    >
      {/* Row header */}
      <Flex
        align="center"
        gap="10px"
        px="16px"
        py="12px"
        cursor="pointer"
        _hover={{ bg: "gray.50" }}
        onClick={() => setOpen((v) => !v)}
        userSelect="none"
      >
        <MethodBadge method={endpoint.method} />
        <Text
          fontWeight="600"
          fontSize="13px"
          color="#1E293B"
          fontFamily="monospace"
          flexShrink={0}
        >
          {endpoint.path}
        </Text>
        <Text fontSize="13px" color="gray.500" flex={1} noOfLines={1}>
          {endpoint.description}
        </Text>
        <Box color="gray.400" flexShrink={0}>
          {open ? (
            <ChevronUpIcon boxSize="16px" />
          ) : (
            <ChevronDownIcon boxSize="16px" />
          )}
        </Box>
      </Flex>

      {/* Expanded details */}
      <Collapse in={open} animateOpacity>
        <Box
          px="24px"
          pb="20px"
          pt="4px"
          bg="gray.50"
          borderTop="1px solid"
          borderColor="gray.100"
        >
          {/* Headers */}
          {endpoint.headers && (
            <Box mb="16px">
              <Text
                fontSize="11px"
                fontWeight="700"
                color="gray.500"
                textTransform="uppercase"
                letterSpacing="0.8px"
                mb="8px"
              >
                Headers
              </Text>
              <Box
                border="1px solid"
                borderColor="gray.200"
                borderRadius="10px"
                overflow="hidden"
              >
                {Object.entries(endpoint.headers).map(
                  ([name, value], i, arr) => (
                    <Flex
                      key={name}
                      align="flex-start"
                      px="12px"
                      py="8px"
                      gap="10px"
                      bg={i % 2 === 0 ? "white" : "gray.50"}
                      borderBottom={
                        i < arr.length - 1 ? "1px solid" : undefined
                      }
                      borderColor="gray.100"
                    >
                      <Text
                        fontFamily="monospace"
                        fontSize="12px"
                        fontWeight="600"
                        color="#1E293B"
                        minW="160px"
                      >
                        {name}
                      </Text>
                      <Badge fontSize="10px" colorScheme="green" flexShrink={0}>
                        header
                      </Badge>
                      <Text
                        fontSize="12px"
                        color="gray.500"
                        fontFamily="monospace"
                      >
                        {value}
                      </Text>
                    </Flex>
                  )
                )}
              </Box>
            </Box>
          )}

          {/* Params */}
          {endpoint.params && endpoint.params.length > 0 && (
            <Box mb="16px">
              <Text
                fontSize="11px"
                fontWeight="700"
                color="gray.500"
                textTransform="uppercase"
                letterSpacing="0.8px"
                mb="8px"
              >
                Parâmetros
              </Text>
              <Box
                border="1px solid"
                borderColor="gray.200"
                borderRadius="10px"
                overflow="hidden"
              >
                {endpoint.params.map((param, i) => (
                  <Flex
                    key={param.name}
                    align="flex-start"
                    px="12px"
                    py="8px"
                    gap="10px"
                    bg={i % 2 === 0 ? "white" : "gray.50"}
                    borderBottom={
                      i < endpoint.params!.length - 1 ? "1px solid" : undefined
                    }
                    borderColor="gray.100"
                  >
                    <Text
                      fontFamily="monospace"
                      fontSize="12px"
                      fontWeight="600"
                      color="#1E293B"
                      minW="160px"
                    >
                      {param.name}
                      {param.required && (
                        <Box as="span" color="red.400" ml="2px">
                          *
                        </Box>
                      )}
                    </Text>
                    <Badge
                      fontSize="10px"
                      colorScheme={
                        param.in === "path"
                          ? "purple"
                          : param.in === "query"
                            ? "blue"
                            : "orange"
                      }
                      flexShrink={0}
                    >
                      {param.in}
                    </Badge>
                    {param.description && (
                      <Text fontSize="12px" color="gray.500">
                        {param.description}
                      </Text>
                    )}
                  </Flex>
                ))}
              </Box>
            </Box>
          )}

          {/* Request Body */}
          {endpoint.body && (
            <Box mb="16px">
              <Text
                fontSize="11px"
                fontWeight="700"
                color="gray.500"
                textTransform="uppercase"
                letterSpacing="0.8px"
                mb="8px"
              >
                Request Body
              </Text>
              <CodeBlock value={endpoint.body} />
            </Box>
          )}

          {/* Response */}
          {endpoint.response !== undefined && (
            <Box>
              <Text
                fontSize="11px"
                fontWeight="700"
                color="gray.500"
                textTransform="uppercase"
                letterSpacing="0.8px"
                mb="8px"
              >
                Response
              </Text>
              <CodeBlock value={endpoint.response as object} />
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
